// src/pages/1_DD.js
import React, { useState, useEffect, useMemo, useContext, useCallback, useRef } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { ChevronLeftIcon, CheckCircleIcon, XCircleIcon, XIcon, InformationCircleIcon, DocumentTextIcon } from '@heroicons/react/solid';
// import { ChevronLeftIcon, ChevronRightIcon, CheckCircleIcon, XCircleIcon, StopIcon, MicrophoneIcon, XIcon } from '@heroicons/react/solid';
import Breadcrumb from '../../../components/Breadcrumb';
import { Tooltip } from 'react-tooltip';
import InfoTooltip from '../../../components/InfoTooltip';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import { useSectionExitRegeneration } from '../../../hooks/useSectionExitRegeneration';
import { auth } from '../../../firebase';
import { serverTimestamp, doc, runTransaction } from 'firebase/firestore';
import { db } from '../../../firebase';
// import { TriggerRenderDocTemplate } from '../../../utils/TriggerRenderDocTemplate';
import ModuleCompletionDialog, { CancelButton, PrimaryActionButton, GenerateReportButton, SummarySection, DialogMessage } from '../../../components/ModuleCompletionDialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import NonADHDDiagnosticConfirmation from './components/NonADHDDiagnosticConfirmation';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';
import { isADHDProvisionalPrimaryDiagnosis, computeCriterionEPrefill } from '../../../utils/conditionChecks';
import { buildFirstGeneratedFields, buildFirstGeneratedFieldsDotNotation, buildFirstCompletedFields, buildFirstCompletedFieldsDotNotation, appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import { useAuth } from '../../../hooks/useAuth';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { hasActionableOtherConditions } from '../../../utils/diagnosticDecisionRouting';
import { resolvePadStatusOnDdComplete } from '../../../utils/padUnlockUtils';

export default function DiagnosticDecisionNoRDD() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, fetchPatientData, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);

  // Determines whether this user is the clinician assigned to the DD stage.
  // When false the entire page renders in read-only mode.
  const canEdit = useCanEditStage(patientDataModel, 'DD');

  // Tracks whether the clinician has changed any criteria, diagnosis, or
  // notes on this page. Drives selective regeneration gating (skip the
  // Cloud Function call when nothing changed and a paragraph already exists)
  // and the navigation guard's dirty-state registration.
  const [hasChanges, setHasChanges] = useState(false);

  // Note: provisionalPrimaryDiagnosis removed - using isAdhdAfterCI consistently with 1_DD.js
  
  // Shared helper keeps this page in parity with PatientOverview and 1_DD.
  const hasOtherConditions = useMemo(() => (
    hasActionableOtherConditions({
      patientDataModel,
      isFlatDifferentials: wf.isFlatDifferentials,
      skipDifferentialsDecisions: wf.skipDifferentialsDecisions,
    })
  ), [patientDataModel, wf.isFlatDifferentials, wf.skipDifferentialsDecisions]);

    const [isAdhdAfterCI, setIsAdhdAfterCI] = useState(false);

  useEffect(() => {
    const useAdhdFlow = isADHDProvisionalPrimaryDiagnosis(patientDataModel);
    setIsAdhdAfterCI(useAdhdFlow);
  }, [patientDataModel]);

  // Flat-model and deferred-DF orgs always present the ADHD diagnostic
  // decision flow, even if PPD was not ADHD or not set.  This prevents
  // the NonADHDDiagnosticConfirmation component (which relies on PPD
  // from CI section 12) from being shown to flat-model clinicians.
  const shouldShowAdhdFlow = isAdhdAfterCI || wf.isDFDeferred || wf.isFlatDifferentials;

  // Safety-net hook: triggers AI paragraph regeneration on unmanaged exits
  // (breadcrumb, top nav, browser back) and registers dirty state so the
  // navigation guard can warn before losing unsaved changes.
  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: 'Diagnostic Decision',
    sectionTitle: 'Diagnostic Decision',
    hasChanges,
    canEdit,
    patientDataModel,
  });

  const [nextSteps, setNextSteps] = useState('');
  // const [activeRecording, setActiveRecording] = useState(false);
  // const [recognition, setRecognition] = useState(null);
  const textAreaRef = useRef(null);


  const currentSectionNumber = 1

  const SECTIONS_SSI = useMemo(() => Object.entries(patientDataModel['dataModel']['3_semiStructuredInterview']['sections']).map(([key, section]) => ({
    key,
    ...section
  })), [patientDataModel]);
  
  const finalDiagnosisSection = useMemo(() => {
    return patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.[currentSectionNumber] || {};
  }, [patientDataModel, currentSectionNumber]);

  console.log("finalDiagnosisSection", finalDiagnosisSection)

  const questionsSymptoms = useMemo(() => {
    const section = SECTIONS_SSI.find(
      section => section.sectionTitle === "Symptoms"
    );
    if (!section) {
      console.error(`Section with title Symptoms not found.`);
      return [];
    }
    return Object.entries(section.questions || {}).map(([key, question]) => question);
  }, [SECTIONS_SSI]);

  const attentionDeficitData = questionsSymptoms
    ? questionsSymptoms.filter(item => item.DSM_code.startsWith('A1')).map(item => ({
      criterion: item.DSM_code,
      symptom: item.DSM_symptom,
      adulthood: item.symptomPresent_Adulthood,
      childhood: item.symptomPresent_Childhood,
      presentInSettings_Adulthood: item.presentInSettings_Adulthood,
    }))
    : [];

  const hyperactivityImpulsivityData = questionsSymptoms
    ? questionsSymptoms.filter(item => item.DSM_code.startsWith('A2')).map(item => ({
      criterion: item.DSM_code,
      symptom: item.DSM_symptom,
      adulthood: item.symptomPresent_Adulthood,
      childhood: item.symptomPresent_Childhood,
      presentInSettings_Adulthood: item.presentInSettings_Adulthood,
    }))
    : [];



  const calculateTotals = (data) => {
    const adulthoodTotal = data.filter(item => item.adulthood).length;
    const childhoodTotal = data.filter(item => item.childhood).length;
    return { adulthoodTotal, childhoodTotal };
  };

  const attentionTotals = calculateTotals(attentionDeficitData);
  const hyperactivityTotals = calculateTotals(hyperactivityImpulsivityData);


  const dataDomains = useMemo(() => {
    const section = SECTIONS_SSI.find(
      section => section.sectionTitle === "Domains of Functional Impairment"
    );
    if (!section) {
      console.error(`Section with title Domains of Functional Impairment not found.`);
      return [];
    }
    return section
  }, [SECTIONS_SSI]);

  const domainImpairment = useMemo(() => {
    if (!dataDomains?.questions) return { 
      dsmCriterionD_FunctionalImpairment: false
    };
    
    let adulthoodDomainsCount = 0;
  
    // Iterate through each question
    Object.values(dataDomains.questions).forEach(question => {
      // Check if there are any examples selected for adulthood
      if (question.functioningReduced_Adulthood)  {
        adulthoodDomainsCount++;
      }      
    });
  
    return {
      dsmCriterionD_FunctionalImpairment: adulthoodDomainsCount >= 1
    };
  }, [dataDomains]);
  
  // Calculate if criterion C is met (symptoms across settings)
  const criterionCMet = useMemo(() => {
    // Skip if no symptom data
    if (!questionsSymptoms || questionsSymptoms.length === 0) return null;

    // Count symptoms present in each setting
    const settingCounts = {
      'Familial': 0,
      'Social': 0,
      'Occupational': 0,
      'Educational': 0,
      'Community': 0
    };  

    // Iterate through all symptoms
    questionsSymptoms.forEach(symptom => {
      // Only count symptoms present in adulthood
      if (symptom.symptomPresent_Adulthood === true) {
        // Check which settings this symptom occurs in
        const presentInSettings = symptom.presentInSettings_Adulthood || [];
        
        // Increment counter for each setting this symptom is present in
        presentInSettings.forEach(setting => {
          if (setting in settingCounts) {
            settingCounts[setting]++;
          }
        });
      }
    });

    // Count how many settings have at least 3 symptoms
    const settingsWithThreePlusSymptoms = Object.values(settingCounts).filter(count => count >= 3).length;
    
    // Log for debugging
    // console.log("Setting counts:", settingCounts, "Settings with 3+ symptoms:", settingsWithThreePlusSymptoms);
    
    // Criterion C is met if at least 2 settings have 3+ symptoms
    return settingsWithThreePlusSymptoms >= 2;
  }, [questionsSymptoms]);

  // Criterion E prefill: no RDD exists for this page variant, so compute
  // directly from the workflow config, DF model and patient data.
  const criterionEPrefill = useMemo(
    () => computeCriterionEPrefill(wf, patientDataModel),
    [wf, patientDataModel]
  );

  const questionPanels = useMemo(() => [
    {
      title: "Criterion A: Symptom Threshold",
      questions: [
        {
          name: 'Are ≥5 inattention symptoms present in adulthood?',
          prefill: attentionTotals.adulthoodTotal >= 5,
          key: "dsmCriterionA_SymptomThreshold_A",
          isOptional: false,
        },
        {
          name: 'Are ≥5 hyperactivity-impulsivity symptoms present in adulthood?',
          prefill: hyperactivityTotals.adulthoodTotal >= 5,
          key: "dsmCriterionA_SymptomThreshold_HI",
          isOptional: false,
        },
      ],
    },
    {
      title: "Criterion B: Age of Onset",
      questions: [
        {
          name: 'Were several (≥3) symptoms of inattention and/or hyperactivity-impulsivity present before age 12?',
          prefill: attentionTotals.childhoodTotal + hyperactivityTotals.childhoodTotal >= 3,
          key: "dsmCriterionB_AgeOfOnset",
          isOptional: false,
        },
      ],
    },
    {
      title: "Criterion C: Symptoms Across Settings",
      questions: [
        {
          name: 'Are several (≥3) symptoms present in each of at least ≥2 settings?',
          prefill: criterionCMet,
          key: "dsmCriterionC_SymptomsAcrossSettings",
          isOptional: false,
        },
      ],
    },
    {
      title: "Criterion D: Functional Impairment",
      questions: [
        {
          name: 'Do the symptoms cause at least moderate psychological, social, and/or educational or occupational impairment?',
          prefill: domainImpairment.dsmCriterionD_FunctionalImpairment,
          key: "dsmCriterionD_FunctionalImpairment",
          isOptional: false,
        },
      ],
    },
    {
      title: "Criterion E: Exclusion of Other Disorders",
      questions: [
        {
          name: 'The symptoms cannot be (better) explained by another psychiatric disorder and are not due to the effect of a substance or medication',
          prefill: criterionEPrefill.value,
          key: "dsmCriterionE_NotOtherExplanation",
          isOptional: false,
        },
      ],
      tooltip: "The symptoms do not occur exclusively during the course of schizophrenia or another psychotic disorder and are not better explained by another mental disorder (e.g., mood disorder, anxiety disorder, dissociative disorder, personality disorder, substance intoxication or withdrawal).",
    },
    {
      title: "Diagnostic Decision",
      questions: [
        {
          id: 'diagnose_adhd',
          name: 'Has the criteria for ADHD Diagnosis Been Met?',
          radio_options: [
            {
              value: 'Fully met'
            },
            {
              value: 'Partially met',
              tooltip: "Significant symptoms and impairment present"
            },
            {
              value: 'Not met',
              tooltip: "Criteria not met for ADHD diagnosis"
            }
          ],
          prefill: null,
          key: "diagnosisDecision",
          isOptional: false,
          recommendedSelection: null,
        },
        {
          id: 'select_adhd_subtype',
          name: 'Recommended Presentation',
          subtitle: "Select the appropriate presentation based on current symptoms",
          radio_options: [
            {
              value: 'Predominantly Inattentive Presentation',
              tooltip: "≥5 inattention symptoms but <5 hyperactive-impulsive symptoms in adulthood"
            },
            {
              value: 'Predominantly Hyperactive-Impulsive Presentation',
              tooltip: "≥5 hyperactive-impulsive symptoms but <5 inattention symptoms in adulthood"
            },
            {
              value: 'Combined Presentation',
              tooltip: "≥5 inattention and ≥5 hyperactive-impulsive symptoms in adulthood"
            }
          ],
          prefill: null,
          dependence: ['diagnose_adhd'],
          showWhen: (selections) => selections[5][0] === 'Fully met',
          key: "diagnosisPresentation",
          isOptional: false,
          recommendedSelection: null,
        },
        {
          id: 'select_adhd_severity',
          name: 'Recommended Severity of Symptoms',
          subtitle: "Select the appropriate level of severity based on the extent of impairment in daily life",
          radio_options: [
            {
              value: 'Moderate',
              tooltip: "Symptoms cause noticeable impairment in multiple domains"
            },
            {
              value: 'Severe',
              tooltip: "Symptoms cause significant disruption to daily functioning"
            }
          ],
          prefill: null,
          dependence: ['diagnose_adhd'],
          showWhen: (selections) => selections[5][0] === 'Fully met',
          key: "diagnosisSeverity",
          isOptional: false,
          recommendedSelection: null,
        },
        {
          id: 'select_partial_adhd_type',
          name: 'Recommended Diagnostic Classification',
          subtitle: "Select the appropriate classification for partial criteria",
          radio_options: [
            {
              value: 'ADHD in Partial Remission',
              tooltip: "Requires prior ADHD diagnosis; symptoms now fall below the threshold but impairment persists in social, academic, or occupational functioning."
            },
            {
              value: 'Other Specified ADHD',
              tooltip: "Symptoms cause significant impairment but do not meet full ADHD criteria. Clinician must specify the reason."
            },
            {
              value: 'Unspecified ADHD',
              tooltip: "Used when insufficient information/time to clarify (e.g., emergency settings)."
            }
          ],
          prefill: null,
          dependence: ['diagnose_adhd'],
          showWhen: (selections) => selections[5][0] === 'Partially met',
          key: "partialDiagnosisClassification",
          isOptional: false,
          recommendedSelection: null,
        },
        {
          id: 'specify_other_adhd_reason',
          name: 'Specify Reason for Other Specified ADHD',
          subtitle: "Select the reason this patient meets Other Specified ADHD criteria",
          radio_options: [
            {
              value: 'Subthreshold symptoms with impairment'
            },
            {
              value: 'Atypical symptom patterns'
            },
            {
              value: 'Late onset symptoms'
            },
            {
              value: 'Symptom overlap with another disorder'
            },
            {
              value: 'Symptoms that cause impairment in one setting'
            },
            {
              value: 'Symptom variability'
            },
            {
              value: 'Other'
            }
          ],
          prefill: null,
          dependence: ['select_partial_adhd_type'],
          showWhen: (selections) => {
            const diagnosisDecision = selections[5][0];
            const partialType = selections[5][3]; // Index of partialDiagnosisClassification
            return diagnosisDecision === 'Partially met' && partialType === 'Other Specified ADHD';
          },
          key: "otherSpecifiedReason",
          isOptional: false,
          recommendedSelection: null,
        },
        {
          id: 'other_reason_text',
          name: 'Specify Other Reason',
          subtitle: "Please provide details for the other reason selected above",
          prefill: null,
          dependence: ['specify_other_adhd_reason'],
          showWhen: (selections) => {
            const diagnosisDecision = selections[5][0];
            const partialType = selections[5][3]; // Index of partialDiagnosisClassification
            const otherReason = selections[5][4]; // Index of otherSpecifiedReason
            return diagnosisDecision === 'Partially met' &&
              partialType === 'Other Specified ADHD' &&
              otherReason === 'Other';
          },
          isTextInput: true,
          key: "otherSpecifiedReasonText",
          isOptional: false,
          recommendedSelection: null,
        },
        {
          id: 'select_supporting_evidence',
          name: 'Supporting Evidence',
          subtitle: "Is the diagnosis supported by collateral information?",
          checklist_options: [
            {
              value: 'Informant reports',
            },
            {
              value: 'Employer report',
            },
            {
              value: 'School/work records',
            },
            {
              value: 'Medical records',
            },
            {
              value: 'None available',
            }
          ],
          prefill: null,
          showWhen: (selections) => selections[5][0] === 'Fully met' || selections[5][0] === 'Partially met',
          key: "supportedByCollateral",
          isOptional: true,
          recommendedSelection: null,
        },
      ],
    },
], [attentionTotals, hyperactivityTotals, domainImpairment, criterionCMet, criterionEPrefill]);


  const [selections, setSelections] = useState(() => {
    // Get saved selections from localStorage as fallback (currently commented out)
    // const savedSelections = localStorage.getItem(`selections_${patientId}`);

    // Initialize selections array based on questionPanels structure
    return questionPanels.map(panel =>
      panel.questions.map(question => {
        // If question has a key, check for value in finalDiagnosisSection first
        if (question.key && finalDiagnosisSection) {
          // Special case for supportedByCollateral which should default to [].
          // Normalize legacy "Partner/family reports" so existing selections
          // are preserved after the label was renamed to "Informant reports".
          if (question.key === 'supportedByCollateral') {
            const raw = finalDiagnosisSection[question.key] ?? [];
            return raw.map(v => v === 'Partner/family reports' ? 'Informant reports' : v);
          }

          // For all other keys, return finalDiagnosisSection value if available,
          // otherwise fallback to prefill value or null
          return finalDiagnosisSection[question.key] ?? question.prefill ?? null;
        }

        // For questions without keys, just use prefill value or null
        return question.prefill ?? null;
      })
    );
  });

  const handleSelection = async (panelIndex, questionIndex, value) => {
    if (!canEdit) return;
    const newSelections = [...selections];

    // If this is the diagnosis decision question (index 5, question 0)
    if (panelIndex === 5 && questionIndex === 0) {
      // Update the main selection
      newSelections[panelIndex][questionIndex] = value;

      // Reset dependent selections based on the new value
      if (value === 'Fully met') {
        // Reset presentation type and severity
        newSelections[panelIndex][1] = null; // Reset subtype
        newSelections[panelIndex][2] = null; // Reset severity
        // Clear partial met options
        newSelections[panelIndex][3] = null; // Reset partial ADHD type
        newSelections[panelIndex][4] = null; // Reset other specified reason
        newSelections[panelIndex][5] = null; // Reset other reason text
      }
      else if (value === 'Partially met') {
        // Clear fully met options
        newSelections[panelIndex][1] = null; // Clear subtype
        newSelections[panelIndex][2] = null; // Clear severity
        // Reset partial options
        newSelections[panelIndex][3] = null; // Reset partial ADHD type
      }
      else {
        // Not met - clear all dependent fields
        newSelections[panelIndex][1] = null;
        newSelections[panelIndex][2] = null;
        newSelections[panelIndex][3] = null;
        newSelections[panelIndex][4] = null;
        newSelections[panelIndex][5] = null;
        newSelections[panelIndex][6] = null; // Supporting evidence
      }

      // Update state
      setSelections(newSelections);

      // Update Firestore with changes
      const updateData = {
        diagnosisDecision: value,
        diagnosisPresentation: null,
        diagnosisSeverity: null,
        partialDiagnosisClassification: null,
        otherSpecifiedReason: null,
        otherSpecifiedReasonText: null,
        supportedByCollateral: value === 'Not met' ? null : newSelections[panelIndex][6],
      };

      await updatePatientFirestore(patientId, {
        '4_diagnosticDecision': {
          sections: {
            [currentSectionNumber]: updateData
          },
        },
      });
    }
    // If this is the partial ADHD type question (index 5, question 3)
    else if (panelIndex === 5 && questionIndex === 3) {
      newSelections[panelIndex][questionIndex] = value;

      // If not "Other Specified ADHD", clear other specified reason fields
      if (value !== 'Other Specified ADHD') {
        newSelections[panelIndex][4] = null; // Reset other specified reason
        newSelections[panelIndex][5] = null; // Reset other reason text
      }

      setSelections(newSelections);

      // Update Firestore
      await updatePatientFirestore(patientId, {
        '4_diagnosticDecision': {
          sections: {
            [currentSectionNumber]: {
              partialDiagnosisClassification: value,
              otherSpecifiedReason: value !== 'Other Specified ADHD' ? null : newSelections[panelIndex][4],
              otherSpecifiedReasonText: value !== 'Other Specified ADHD' ? null : newSelections[panelIndex][5],
            },
          },
        },
      });
    }
    // If this is the other specified reason (index 5, question 4)
    else if (panelIndex === 5 && questionIndex === 4) {
      newSelections[panelIndex][questionIndex] = value;

      // If not "Other", clear the other reason text
      if (value !== 'Other') {
        newSelections[panelIndex][5] = null;
      }

      setSelections(newSelections);

      // Update Firestore
      await updatePatientFirestore(patientId, {
        '4_diagnosticDecision': {
          sections: {
            [currentSectionNumber]: {
              otherSpecifiedReason: value,
              otherSpecifiedReasonText: value !== 'Other' ? null : newSelections[panelIndex][5],
            },
          },
        },
      });
    }
    else {
      // Handle other selections normally
      newSelections[panelIndex][questionIndex] = value;
      setSelections(newSelections);

      // Get the corresponding question key from questionPanels
      const question = questionPanels[panelIndex].questions[questionIndex];

      // Update Firestore with the specific field
      await updatePatientFirestore(patientId, {
        '4_diagnosticDecision': {
          sections: {
            [currentSectionNumber]: {
              [question.key]: value,
            },
          },
        },
      });
    }

    // Also save to localStorage as backup
    localStorage.setItem(`selections_${patientId}`, JSON.stringify(newSelections));
    setHasChanges(true);
  };

  const isFormValid = useCallback(() => {
    // Early return if any required data is missing
    if (!selections || !questionPanels || !Array.isArray(selections) || !Array.isArray(questionPanels)) {
      console.log('Missing required data for validation:', { selections, questionPanels });
      return false;
    }

    try {
      // Get diagnostic decision        
      const diagnosisDecision = selections[5][0];

      // If no diagnosis decision selected, form is invalid
      if (!diagnosisDecision) return false;

      // Based on diagnosis decision, validate required fields
      if (diagnosisDecision === 'Fully met') {
        // For 'Fully met', need presentation type and severity
        const subtypeSelected = selections[5][1]; // Presentation type
        const severitySelected = selections[5][2]; // Severity
        if (!subtypeSelected || !severitySelected) return false;
      }
      else if (diagnosisDecision === 'Partially met') {
        // For 'Partially met', need partial ADHD type
        const partialTypeSelected = selections[5][3]; // Partial ADHD type
        if (!partialTypeSelected) return false;

        // If 'Other Specified ADHD' selected, need reason
        if (partialTypeSelected === 'Other Specified ADHD') {
          const reasonSelected = selections[5][4]; // Other specified reason
          if (!reasonSelected) return false;

          // If 'Other' reason selected, need text explanation
          if (reasonSelected === 'Other') {
            const otherText = selections[5][5];
            // Check if the text is null, undefined, or just whitespace
            if (!otherText || otherText.trim() === '') return false;
          }
        }
      }
      else if (diagnosisDecision === 'Not met') {
        // For 'Not met', allow button to be clickable even without next steps
        // The next steps will be required in the dialog
        return true;
      }

      // If we reach here, form is valid
      return true;
    } catch (error) {
      console.error('Error in form validation:', error);
      return false;
    }
  }, [selections, questionPanels]);

  const [isSaving, setIsSaving] = useState(false);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const handleNavigationChoice = async (choice) => {
    if (!canEdit) return;
    setIsNavigating(true);
    markRegenHandled();

    const diagnosisDecision = selections[5][0];

    if (diagnosisDecision === 'Not met' && nextSteps && nextSteps.trim()) {
      updatePatientFirestore(patientId, {
        '4_diagnosticDecision': {
          sections: {
            [currentSectionNumber]: {
              clinicianNotes: nextSteps,
            },
          },
        },
      });
    }

    const dataReady = flushPendingUpdates({ noTimeout: true });

    const generated = patientDataModel?.dataModel?.['6_manageReports']
      ?.sections?.['3']?.stageSectionsGenerated || [];
    const isGenerated = generated.includes('Diagnostic Decision');
    if (hasChanges || !isGenerated) {
      TriggerGenerateDocumentSection(patientId, 'Diagnostic Decision', 'Diagnostic Decision', [], dataReady);
    }

    if (choice === 'secondary') {
      const csdRoute = wf.isFlatDifferentials
        ? `/patient/${patientId}/proforma/diagnostic-decision/other-conditions`
        : `/patient/${patientId}/proforma/diagnostic-decision/secondary-diagnoses`;
      navigate(csdRoute);
      return;
    }

    try {
      await dataReady;

      const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
      const ddFirstFields = buildFirstGeneratedFieldsDotNotation('dd', manageReports);
      const ddFirstCompletedFields = buildFirstCompletedFieldsDotNotation('4_diagnosticDecision', patientDataModel?.dataModel?.['4_diagnosticDecision']);
      const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['4_diagnosticDecision']);

      const padStatusUpdate = resolvePadStatusOnDdComplete(
        patientDataModel,
        shouldShowAdhdFlow || wf.isSSIAlwaysExpected
      );

      try {
        await runTransaction(db, async (transaction) => {
          const docRef = doc(db, 'patients', patientId);
          const updates = {
            'dataModel.4_diagnosticDecision.status': 'completed',
            'dataModel.4_diagnosticDecision.progress': 100,
            'dataModel.4_diagnosticDecision.docs_available': true,
            'dataModel.4_diagnosticDecision.lastCompletedAt': serverTimestamp(),
            'dataModel.4_diagnosticDecision.lastCompletedBy': auth.currentUser.uid,
            'dataModel.4_diagnosticDecision.lastCompletedByName': auth.currentUser.displayName,
            ...ddFirstCompletedFields,
            'dataModel.6_manageReports.ddReportGenerated': true,
            'dataModel.6_manageReports.ddReportGeneratedAt': serverTimestamp(),
            'dataModel.6_manageReports.ddReportGeneratedBy': auth.currentUser.uid,
            'dataModel.6_manageReports.ddReportGeneratedByName': auth.currentUser.displayName,
            ...ddFirstFields,
          };
          if (padStatusUpdate) {
            updates['dataModel.5_postAssessmentDiscussion.status'] = padStatusUpdate;
          }
          transaction.update(docRef, updates);
        });
      } catch (transactionError) {
        console.warn('Direct transaction failed, falling back to updatePatientFirestore:', transactionError);
        const fallback = {
          '4_diagnosticDecision': {
            status: 'completed',
            progress: 100,
            docs_available: true,
            lastCompletedAt: serverTimestamp(),
            lastCompletedBy: auth.currentUser.uid,
            lastCompletedByName: auth.currentUser.displayName,
            ...firstCompletedFields,
          },
          '6_manageReports': {
            ddReportGenerated: true,
            ddReportGeneratedAt: serverTimestamp(),
            ddReportGeneratedBy: auth.currentUser.uid,
            ddReportGeneratedByName: auth.currentUser.displayName,
            ...buildFirstGeneratedFields('dd', manageReports),
          }
        };
        if (padStatusUpdate) {
          fallback['5_postAssessmentDiscussion'] = { status: padStatusUpdate };
        }
        await updatePatientFirestore(patientId, fallback);
      }

      appendReportAuditEntry(patientId, {
        module: 'dd',
        eventType: 'generated',
        isFirstOccurrence: !manageReports?.ddReportFirstGeneratedAt,
      });

      // Inferred appointment audit on first completion (fire-and-forget; does not write appointmentDates)
      recordInferredAppointmentDateOnCompletion({
        patientId,
        moduleKey: 'dd',
        didWriteFirstCompleted: Object.keys(ddFirstCompletedFields).length > 0,
        enabled: wf.isAppointmentDatesEnabled,
      });

      if (choice === 'documents') {
        setIsDialogOpen(false);
        setIsGenerating(true);
      } else if (choice === 'pad') {
        setIsDialogOpen(false);
        navigate(`/patient/${patientId}/proforma/post-assessment-discussion/review-assessment`);
      }

    } catch (error) {
      console.error("Error during navigation:", error);
      setIsNavigating(false);
      alert("An error occurred. Please try again.");
    }
  };

  const handleGenerateReport = async () => {
    await handleNavigationChoice('documents');
  };
  
  const handleGoToSecondaryDiagnoses = async () => {
    await handleNavigationChoice('secondary');
  };
  
  const handleGoToPostAssessmentDiscussion = async () => {
    await handleNavigationChoice('pad');
  };

  const handleCancelGeneration = () => {
    setIsGenerating(false);
  };

  // Separate validation for dialog buttons - checks if next steps are required and filled
  const isDialogFormValid = useCallback(() => {
    if (shouldShowAdhdFlow) {
      const diagnosisDecision = selections[5][0];
      if (diagnosisDecision === 'Not met') {
        // For ADHD 'Not met', require next steps text in the dialog
        return nextSteps && nextSteps.trim() !== '';
      }
    }
    // For non-ADHD cases or ADHD cases that aren't "Not met", dialog form is always valid
    return true;
  }, [selections, nextSteps, shouldShowAdhdFlow]);
  
  // Whether to show the "Generate Report" button in the completion dialog.
  // Shown whenever DD is a terminal step (no secondary diagnoses left to navigate to),
  // giving ALL organisations the option to generate the report and proceed to review.
  const shouldShowGenerateReportButton = useCallback(() => {
    return !hasOtherConditions;
  }, [hasOtherConditions]);

  // Whether to show the "Go to Post-Assessment Discussion" button.
  // PAD is part of the workflow when either the ADHD assessment path was
  // taken OR the org always conducts a full assessment (always-SSI).  For
  // always-SSI orgs, PAD always happens — the DD ADHD outcome only
  // determines full vs partial PAD content.
  const shouldShowPADButton = useCallback(() => {
    return (shouldShowAdhdFlow || wf.isSSIAlwaysExpected) && !hasOtherConditions;
  }, [shouldShowAdhdFlow, wf.isSSIAlwaysExpected, hasOtherConditions]);

  const handleNextPageButtonClick = (route) => {
    navigate(route);
  };

  const constructDiagnosisSummary = useCallback(() => {
    let output = [];
    
    if (!shouldShowAdhdFlow) {
      // For non-ADHD cases (hierarchical orgs only), show the non-ADHD diagnosis decision
      const nonAdhdData = patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.[1] || {};
      const provisionalPrimaryDiagnosis = patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.[12]?.provisionalPrimaryDiagnosis || 'Not specified';
      
      output.push(`• Provisional Primary Diagnosis: ${provisionalPrimaryDiagnosis}`);
      
      if (nonAdhdData.nonAdhdDiagnosisDecision) {
        output.push(`• Diagnosis Confirmed: ${nonAdhdData.nonAdhdDiagnosisDecision}`);
        
        if (nonAdhdData.nonAdhdDiagnosisNotes) {
          output.push(`• Your Notes: ${nonAdhdData.nonAdhdDiagnosisNotes}`);
        }
      }
      
      return output.map((bullet, index) => (
        <div key={index} className="mb-1">
          {bullet}
        </div>
      ));
    }
    
    // For ADHD cases, show the original ADHD decision summary
    const diagnosisDecision = selections[5][0];

    output.push(`• DSM-5 Criteria for ADHD: ${diagnosisDecision || 'Not specified'}`);

    if (diagnosisDecision === 'Fully met') {
      // For fully met criteria, include presentation type and severity
      if (selections[5][1]) output.push(`• Presentation: ${selections[5][1]}`);
      if (selections[5][2]) output.push(`• Severity: ${selections[5][2]}`);
    }
    else if (diagnosisDecision === 'Partially met') {
      // For partially met criteria, include diagnostic classification
      if (selections[5][3]) {
        output.push(`• Classification: ${selections[5][3]}`);

        // If "Other Specified ADHD" was chosen, include the reason
        if (selections[5][3] === 'Other Specified ADHD' && selections[5][4]) {
          const reasonType = selections[5][4];

          if (reasonType === 'Other') {
            // For "Other" reason, display the user-entered text
            const customReasonText = selections[5][5];
            if (customReasonText && customReasonText.trim() !== '') {
              output.push(`• Reason: Other - ${customReasonText}`);
            } else {
              output.push(`• Reason: Other (no details specified)`);
            }
          } else {
            // For standard reason options
            output.push(`• Reason: ${reasonType}`);
          }
        }
      }
    }

    // Add supporting evidence if available for both Fully met and Partially met
    if ((diagnosisDecision === 'Fully met' || diagnosisDecision === 'Partially met') &&
      selections[5][6] &&
      selections[5][6].length > 0) {
      output.push(
        <div className="flex items-start gap-1.5">
          <DocumentTextIcon className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <span><span className="font-semibold">Supporting Evidence:</span> {selections[5][6].join(', ')}</span>
        </div>
      );
    }

    return output.map((bullet, index) => (
      <div key={index} className="mb-1">
        {bullet}
      </div>
    ));
  }, [selections, shouldShowAdhdFlow, patientDataModel]);

  // const handleRecording = () => {
  //   if (!('webkitSpeechRecognition' in window)) {
  //     alert('Speech recognition not supported in this browser.');
  //     return;
  //   }

  //   if (recognition) {
  //     recognition.stop();
  //     return;
  //   }

  //   const newRecognition = new window.webkitSpeechRecognition();
  //   newRecognition.continuous = true;
  //   newRecognition.interimResults = true;
  //   newRecognition.lang = 'en-GB';

  //   newRecognition.onresult = (event) => {
  //     let finalTranscript = nextSteps;
  //     for (let i = event.resultIndex; i < event.results.length; i++) {
  //       let transcript = event.results[i][0].transcript.trim();
  //       if (event.results[i].isFinal) {
  //         transcript = ' ' + transcript.charAt(0).toUpperCase() + transcript.slice(1) + '.';
  //         finalTranscript += transcript;
  //       }
  //     }
  //     setNextSteps(finalTranscript);
  //   };

  //   newRecognition.onend = () => {
  //     setActiveRecording(false);
  //     setRecognition(null);

  //     // Simulate focus and blur to trigger onBlur
  //     if (textAreaRef.current) {
  //       textAreaRef.current.focus();
  //       setTimeout(() => {
  //         textAreaRef.current.blur();
  //       }, 0);
  //     }
  //   };

  //   newRecognition.start();
  //   setRecognition(newRecognition);
  //   setActiveRecording(true);
  // };

  const sendSectionUpdates = useCallback((newUpdates) => {
    updatePatientFirestore(patientId, {
      '4_diagnosticDecision': {
        sections: {
          [currentSectionNumber]: newUpdates
        }
      }
    });
  }, [patientId, currentSectionNumber, updatePatientFirestore]);

  // Add these effects and handlers
  useEffect(() => {
    // Load saved notes from localStorage or Firestore if needed
    const savedNotes = localStorage.getItem(`nextSteps_${patientId}`);
    if (savedNotes) {
      setNextSteps(savedNotes);
    }
  }, [patientId]);

  const handleNotesChange = useCallback((newNotes) => {
    if (!canEdit) return;
    setNextSteps(newNotes);
    localStorage.setItem(`nextSteps_${patientId}`, newNotes);
    setHasChanges(true);
  }, [canEdit, patientId]);

  const handleNotesBlur = () => {
    if (!canEdit) return;
    sendSectionUpdates({ clinicianNotes: nextSteps });
    setHasChanges(true);
  };



  const handleFinishDD = useCallback(async () => {
    if (!canEdit) return;
    setIsSaving(true);

    // NOTE: building `updates` is done INSIDE the try block on purpose. If a
    // clinician clicks the confirm button while `selections` and `questionPanels`
    // are momentarily out of sync (e.g. clicking around quickly before state has
    // settled), `selections[panelIndex][questionIndex]` can throw. If that ran
    // before the try (as it used to), the throw would be uncaught and isSaving
    // would never reset — leaving the forward CTA permanently stuck in the
    // disabled grey "Saving..." state. Keeping it inside the try guarantees the
    // catch resets isSaving. (Mirrors the same fix in 1_DD.js.)
    try {
      const updates = {};

      // Iterate over the questionPanels and selections to prepare updates
      questionPanels.forEach((panel, panelIndex) => {
        panel.questions.forEach((question, questionIndex) => {
          const value = selections[panelIndex]?.[questionIndex];
          updates[question.key] = value;
        });
      });

      // Save form data to Firestore
      await updatePatientFirestore(patientId, {
        '4_diagnosticDecision': {
          sections: {
            [currentSectionNumber]: {
              ...updates,
              status: 'completed',
              progress: 100,
            },
          },
        },
      });

      await fetchPatientData(patientId);
      setIsSaving(false);

      // ALWAYS show dialog - all logic will be handled in dialog buttons
      setIsDialogOpen(true);
      
    } catch (error) {
      console.error("Failed to update Firestore before navigation:", error);
      setIsSaving(false);
    }
  }, [canEdit, patientId, fetchPatientData, selections, updatePatientFirestore, questionPanels]);

  const breadcrumbTuples = [
    ["Patient Overview", `/patient/${patientId}`],
    [wf.isFlatDifferentials ? "Decision for ADHD" : "Diagnostic Decision"]
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />
      <div className="flex-grow p-8">
        <div className="flex justify-between items-center mb-3">

          {/* Back button */}
          <button
            onClick={() => navigate(`/patient/${patientId}`)}
            className="flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300"
          >
            <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
            <span className="font-bold">Back to Patient Overview</span>
          </button>

          {/* Centered heading */}
          <div className="flex items-center">
            <h1 className="text-2xl font-bold">{wf.isFlatDifferentials ? "Decision for ADHD" : "Diagnostic Decision"}</h1>
          </div>

          {/* Forward button — hidden for read-only users */}
          {canEdit && (
            <button
              onClick={() => handleFinishDD()}
              className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ${!isFormValid() || isSaving
                ? (isSaving ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed')
                : 'hover:bg-violet-800 transition duration-100 bg-violet-900 text-white'
                }`}
              disabled={!isFormValid() || isSaving}
            >
              <span className="font-bold">{isSaving ? "Saving Progress..." : (wf.isFlatDifferentials ? "Confirm ADHD Diagnostic Decision" : "Confirm Primary Diagnostic Decision")}</span>
              {isSaving
                ? <ButtonSpinner className="w-5 h-5 ml-2" />
                : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
            </button>
          )}
        </div>


        {shouldShowAdhdFlow ? (
        <div className="flex space-x-4">

          <div className="w-3/5 bg-gray-50 rounded-2xl border border-gray-200 p-6 shadow-md">
            <table className="w-full text-left border-collapse shadow-lg rounded-lg">
              <thead>
                <tr className="bg-violet-900 text-white rounded-t-lg">
                  <th className="p-3 text-center rounded-tl-lg w-1/12">Criterion DSM-5</th>
                  <th className="p-3 w-1/3">Symptom</th>
                  <th className="p-3 text-center w-1/12">Present during adulthood</th>
                  <th className="p-3 text-center rounded-tr-lg w-1/12">Present during childhood</th>
                </tr>
              </thead>
              <tbody>
                {attentionDeficitData.map((item, index) => (
                  <tr key={index} className="bg-violet-100">
                    <td className="p-3 text-sm text-center font-bold">{item.criterion}</td>
                    <td className="p-3 text-xs">{item.symptom}</td>
                    <td className="p-3 text-center bg-white">
                      {item.adulthood ? <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" /> : <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" />}
                    </td>
                    <td className="p-3 text-center bg-white">
                      {item.childhood ? <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" /> : <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" />}
                    </td>
                  </tr>
                ))}
                <tr className="bg-violet-300 font-bold">
                  <td className="p-3" colSpan="2">Total criteria: Attention Deficit</td>
                  <td className="p-3 text-center">{attentionTotals.adulthoodTotal} / 9</td>
                  <td className="p-3 text-center">{attentionTotals.childhoodTotal} / 9</td>
                </tr>
                {hyperactivityImpulsivityData.map((item, index) => (
                  <tr key={index} className="bg-violet-100">
                    <td className="p-3 text-sm text-center font-bold">{item.criterion}</td>
                    <td className="p-3 text-xs">{item.symptom}</td>
                    <td className="p-3 text-center bg-white">
                      {item.adulthood ? <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" /> : <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" />}
                    </td>
                    <td className="p-3 text-center bg-white">
                      {item.childhood ? <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" /> : <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" />}
                    </td>
                  </tr>
                ))}
                <tr className="bg-violet-300 font-bold rounded-bl-lg rounded-br-lg">
                  <td className="p-3" colSpan="2">Total criteria: Hyperactivity / Impulsivity</td>
                  <td className="p-3 text-center">{hyperactivityTotals.adulthoodTotal} / 9</td>
                  <td className="p-3 text-center">{hyperactivityTotals.childhoodTotal} / 9</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="w-1/2 bg-gray-50 rounded-2xl border border-gray-200 p-6 shadow-md">
            {questionPanels.map((panel, panelIndex) => (
              <div
                key={panelIndex}
                className={`${panelIndex > 0 ? 'border-t border-gray-200' : ''} py-2`}
              >
                <div className="flex items-center mb-2 gap-2">
                  <h4 className="font-semibold text-lg text-left">{panel.title}</h4>
                  {panel.tooltip && (
                    <InfoTooltip 
                      content={panel.tooltip} 
                      label={`Information about ${panel.title}`}
                      maxWidth="300px"
                      className="flex-shrink-0"
                    />
                  )}
                </div>

                {/* Criterion E prefill reason banner */}
                {panelIndex === 4 && criterionEPrefill.reason && (
                  <div className="mt-1 mb-2">
                    <div className="rounded-2xl p-4 text-left" style={{ backgroundColor: 'rgba(67, 33, 62, 0.08)' }}>
                      <div className="flex items-start">
                        <InformationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#43213e' }} />
                        <p className="ml-3 text-sm" style={{ color: '#43213e' }}>
                          {criterionEPrefill.reason}
                          {criterionEPrefill.cta && <>{' '}<span className="font-bold">{criterionEPrefill.cta}</span></>}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {panel.questions.map((question, questionIndex) => {
                  // First determine if this question should be shown based on its showWhen condition
                  if (question.showWhen && !question.showWhen(selections)) {
                    return null; // Don't render this question at all if showWhen returns false
                  }

                  return (
                    <div key={questionIndex} className={`flex mb-2 text-left`}>
                      <div className={`${(question.radio_options || question.checklist_options || question.isTextInput) ? 'w-1/4 mt-4' : 'w-11/12'} text-sm pr-2 flex flex-col gap-1`}>
                        <div className="flex items-start">
                          <span>{question.name}</span>
                          {question.tooltip && (
                            <InfoTooltip 
                              content={question.tooltip}
                              label="Question information"
                              className="flex-shrink-0 ml-1"
                            />
                          )}
                        </div>
                        {/* Show instruction as subtitle if it exists */}
                        {question.subtitle && (
                          <span className="text-xs text-gray-600 italic">
                            {question.subtitle}
                          </span>
                        )}
                      </div>

                      {/* Question inputs section */}
                      <div className={`flex flex-col ${(question.radio_options || question.checklist_options || question.isTextInput) ? 'w-full mt-4' : ''}`}>
                        {question.isTextInput ? (
                          <div className={`w-full ${question.recommendedSelection ? 'border-violet-900 border-2 border-dashed rounded-lg p-0.5' : ''
                            }`}                            
                          >
                            <textarea
                              ref={(el) => { if (el) { el.style.height = '0px'; el.style.height = el.scrollHeight + 'px'; } }}
                              value={selections[panelIndex][questionIndex] || question.recommendedSelection || ''}
                              onChange={(e) => canEdit && handleSelection(panelIndex, questionIndex, e.target.value)}
                              className={`w-full p-2 border border-gray-300 rounded-md ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                              style={{ overflow: 'hidden', resize: 'none' }}
                              rows={3}
                              placeholder={"Please specify..."}
                              onInput={(e) => { e.target.style.height = '0px'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                              disabled={!canEdit}
                            />
                            {question.recommendedSelection && (
                              <Tooltip
                                id={`recommendation-text-${panelIndex}-${questionIndex}`}
                                place="top"
                                style={{
                                  maxWidth: '300px',
                                  whiteSpace: 'normal',
                                  backgroundColor: 'rgba(34, 34, 34, 1)',
                                  borderRadius: '15px',
                                  color: '#fff',
                                  fontSize: '0.8em'
                                }}
                              />
                            )}
                          </div>
                        ) : question.radio_options ? (
                          question.radio_options.map((option, optionIndex) => {
                            const optionValue = typeof option === 'object' ? option.value : option;
                            const optionTooltip = typeof option === 'object' ? option.tooltip : null;

                            // Check if this option is the recommended one
                            const isRecommended = question.recommendedSelection === optionValue;

                            return (
                              <div
                                key={optionIndex}
                                className={`flex items-center justify-between px-2 py-1 rounded-lg transition-all duration-200 
                                ${selections[panelIndex][questionIndex] === optionValue
                                    ? 'bg-green-200 border-gray-600 border-2'
                                    : 'border border-transparent'
                                  }
                                ${isRecommended
                                    ? 'border-violet-900 border-2 border-dashed'
                                    : ''
                                  }`}                                
                              >
                                <label className={`flex items-center text-sm grow ${canEdit ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                                  <input
                                    type="radio"
                                    name={question.id || `question-${panelIndex}-${questionIndex}`}
                                    value={optionValue}
                                    checked={selections[panelIndex][questionIndex] === optionValue}
                                    onChange={() => canEdit && handleSelection(panelIndex, questionIndex, optionValue)}
                                    className="mr-2 accent-green-600"
                                    disabled={!canEdit}
                                  />
                                  <div className="flex items-center">
                                    <span>{optionValue}</span>
                                    {optionTooltip && (
                                      <InfoTooltip 
                                        content={optionTooltip}
                                        label={`Information about option ${optionValue}`}
                                        side="right"
                                        className="flex-shrink-0 ml-1"
                                      />
                                    )}
                                  </div>
                                </label>
                                {canEdit && selections[panelIndex][questionIndex] === optionValue && (
                                  <button
                                    onClick={() => handleSelection(panelIndex, questionIndex, null)}
                                    className="text-gray-500 hover:text-gray-700 text-sm ml-2"
                                    aria-label="Clear selection"
                                  >
                                    <XIcon className="w-4 h-4" />
                                  </button>
                                )}
                                {isRecommended && (
                                  <Tooltip
                                    id={`recommendation-${panelIndex}-${questionIndex}-${optionIndex}`}
                                    place="top"
                                    style={{
                                      maxWidth: '300px',
                                      whiteSpace: 'normal',
                                      backgroundColor: 'rgba(34, 34, 34, 1)',
                                      borderRadius: '15px',
                                      color: '#fff',
                                      fontSize: '0.8em'
                                    }}
                                  />
                                )}
                              </div>
                            );
                          })
                        ) : question.checklist_options ? (
                          <div className="space-y-1">
                            {question.checklist_options.map((option, optionIndex) => {
                              const isNoneAvailable = option.value === 'None available';
                              const currentSelections = Array.isArray(selections[panelIndex][questionIndex])
                                ? selections[panelIndex][questionIndex]
                                : [];
                              const isSelected = currentSelections.includes(option.value);
                              const isDisabledByNone = !isNoneAvailable && currentSelections.includes('None available');
                              const isDisabledByOthers = isNoneAvailable && currentSelections.length > 0 && !isSelected;

                              // Check if this option is recommended
                              const isRecommended = Array.isArray(question.recommendedSelection) &&
                                question.recommendedSelection.includes(option.value);

                              return (
                                <div
                                  key={optionIndex}
                                  className={`
                                  flex items-center p-1 rounded-lg transition-all duration-200
                                  ${isSelected ? 'bg-green-200 border-gray-600 border-2' : 'border border-transparent'}
                                  ${(isDisabledByNone || isDisabledByOthers) ? 'opacity-50' : ''}
                                  ${isRecommended ? 'border-violet-900 border-2 border-dashed' : ''}
                                `}                                  
                                >
                                  <label className={`flex items-center text-sm grow ${canEdit ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      disabled={!canEdit || isDisabledByNone || isDisabledByOthers}
                                      onChange={() => {
                                        let newSelections = [...currentSelections];

                                        if (isNoneAvailable) {
                                          // If "None available" is clicked
                                          newSelections = isSelected ? [] : ['None available'];
                                        } else {
                                          // For other options
                                          if (isSelected) {
                                            // Remove if already selected
                                            newSelections = newSelections.filter(s => s !== option.value);
                                          } else {
                                            // Add new selection and remove "None available" if present
                                            newSelections = newSelections
                                              .filter(s => s !== 'None available')
                                              .concat(option.value);
                                          }
                                        }

                                        handleSelection(panelIndex, questionIndex, newSelections);
                                      }}
                                      className="mr-2 accent-green-600 h-3 w-3"
                                    />
                                    <span>{option.value}</span>
                                  </label>
                                  {isRecommended && (
                                    <Tooltip
                                      id={`recommendation-checklist-${panelIndex}-${questionIndex}-${optionIndex}`}
                                      place="top"
                                      style={{
                                        maxWidth: '300px',
                                        whiteSpace: 'normal',
                                        backgroundColor: 'rgba(34, 34, 34, 1)',
                                        borderRadius: '15px',
                                        color: '#fff',
                                        fontSize: '0.8em'
                                      }}
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="flex gap-3">
                            <button
                              onClick={() => canEdit && handleSelection(panelIndex, questionIndex, true)}
                              disabled={!canEdit}
                              className={`grow shrink basis-0 h-9 px-8 py-2 rounded-lg border border-gray-400 justify-center items-center gap-1 flex 
                              ${selections[panelIndex][questionIndex] === true ? 'bg-green-200 border-gray-600 border-2' : 'bg-white border-gray-300'} 
                              ${question.recommendedSelection === true ? 'border-violet-900 border-2 border-dashed' : ''}
                              ${!canEdit ? 'cursor-not-allowed opacity-75' : ''}
                              transition-all duration-300`}                              
                            >
                              <div className="px-0.5 justify-center items-center flex">
                                <div className="text-slate-700 text-sm font-semibold leading-tight">Yes</div>
                              </div>
                            </button>
                            <button
                              onClick={() => canEdit && handleSelection(panelIndex, questionIndex, false)}
                              disabled={!canEdit}
                              className={`grow shrink basis-0 h-9 px-8 py-2 rounded-lg border border-gray-400 justify-center items-center gap-1 flex 
                              ${selections[panelIndex][questionIndex] === false ? 'bg-red-200 border-gray-600 border-2' : 'bg-white'} 
                              ${question.recommendedSelection === false ? 'border-violet-900 border-2 border-dashed' : ''}
                              ${!canEdit ? 'cursor-not-allowed opacity-75' : ''}
                              transition-all duration-300`}                              
                            >
                              <div className="px-0.5 justify-center items-center flex">
                                <div className="text-slate-700 text-sm font-semibold leading-tight">No</div>
                              </div>
                            </button>
                            {question.recommendedSelection === true && (
                              <Tooltip
                                id={`recommendation-${panelIndex}-${questionIndex}-yes`}
                                place="top"
                                style={{
                                  maxWidth: '300px',
                                  whiteSpace: 'normal',
                                  backgroundColor: 'rgba(34, 34, 34, 1)',
                                  borderRadius: '15px',
                                  color: '#fff',
                                  fontSize: '0.8em'
                                }}
                              />
                            )}
                            {question.recommendedSelection === false && (
                              <Tooltip
                                id={`recommendation-${panelIndex}-${questionIndex}-no`}
                                place="top"
                                style={{
                                  maxWidth: '300px',
                                  whiteSpace: 'normal',
                                  backgroundColor: 'rgba(34, 34, 34, 1)',
                                  borderRadius: '15px',
                                  color: '#fff',
                                  fontSize: '0.8em'
                                }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

              </div>
            ))}
          </div>

        </div>
        ) : (
          // New component for non-ADHD diagnoses
          <NonADHDDiagnosticConfirmation canEdit={canEdit} onDirty={() => setHasChanges(true)} />
        )}
       
       <div className="flex justify-end items-center mt-8 pt-4 border-t border-gray-200">
        {canEdit && (
          <button
            onClick={() => handleFinishDD()}
            className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ${!isFormValid() || isSaving
              ? (isSaving ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed')
              : 'hover:bg-violet-800 transition duration-100 bg-violet-900 text-white'
              }`}
            disabled={!isFormValid() || isSaving}
          >
            <span className="font-bold">{isSaving ? "Saving Progress..." : (wf.isFlatDifferentials ? "Confirm ADHD Diagnostic Decision" : "Confirm Primary Diagnostic Decision")}</span>
            {isSaving
              ? <ButtonSpinner className="w-5 h-5 ml-2" />
              : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
          </button>
        )}
       </div>

      </div>
      {/* Module completion dialog for the primary Diagnostic Decision (no RDD variant).
          Button ordering is MDT-aware — same pattern as 1_DD.js. */}
      <ModuleCompletionDialog
        open={isDialogOpen && !isGenerating}
        onOpenChange={setIsDialogOpen}
        title={shouldShowAdhdFlow ? "ADHD Diagnostic Decision Complete" : "Diagnostic Decision Complete"}
        description={shouldShowAdhdFlow ? "Review your ADHD diagnostic decision before proceeding." : "Review your diagnostic decision before proceeding."}
        actions={(() => {
          const canProceed = isDialogFormValid() && !isNavigating;
          const showBothOptions = shouldShowGenerateReportButton() && shouldShowPADButton();

          const generateReportBtn = shouldShowGenerateReportButton() && (
            <GenerateReportButton
              key="generate"
              onClick={() => { if (!isNavigating) handleGenerateReport(); }}
              disabled={!canProceed}
              secondary={showBothOptions && !wf.isMDTReview}
              savingLabel={isNavigating ? "Saving..." : undefined}
            />
          );

          const padBtn = shouldShowPADButton() && (
            <PrimaryActionButton
              key="pad"
              label={isNavigating ? "Saving..." : "Go to Post-Assessment Discussion"}
              onClick={() => { if (!isNavigating) handleGoToPostAssessmentDiscussion(); }}
              disabled={!canProceed}
              secondary={showBothOptions && wf.isMDTReview}
            />
          );

          const secondaryDiagnosesBtn = hasOtherConditions && (
            <PrimaryActionButton
              key="secondary"
              label={isNavigating ? "Saving..." : (wf.isFlatDifferentials ? "Go to Decision for Other Conditions" : "Go to Secondary and/or Comorbid Diagnoses")}
              onClick={() => { if (!isNavigating) handleGoToSecondaryDiagnoses(); }}
              disabled={isNavigating}
            />
          );

          return (
            <>
              <CancelButton onClick={() => setIsDialogOpen(false)} />
              {hasOtherConditions
                ? secondaryDiagnosesBtn
                : wf.isMDTReview
                  ? <>{padBtn}{generateReportBtn}</>
                  : <>{generateReportBtn}{padBtn}</>
              }
            </>
          );
        })()}
      >
        <SummarySection title={shouldShowAdhdFlow ? "Your ADHD Diagnostic Decision:" : "Your Diagnostic Decision:"}>
          {constructDiagnosisSummary()}
        </SummarySection>

        <DialogMessage>
          {(isAdhdAfterCI && selections[5][0] !== "Not met") || (!isAdhdAfterCI)
            ? "Please review your selections. Are you happy to proceed?"
            : (
              <>
                Given your diagnostic decision for ADHD is that the criteria were 'Not met', please provide your recommended next steps before proceeding.
                {hasOtherConditions && (
                  <>
                    <br /><br />
                    <span className="font-bold">
                      You will also be able to record decisions and recommended next steps for other conditions under consideration on the next page.
                    </span>
                  </>
                )}
              </>
            )
          }
        </DialogMessage>

        {isAdhdAfterCI && selections[5][0] === "Not met" && (
          <div className="mb-6">
            <h4 className="font-bold mb-4 text-lg">My recommended next steps are:</h4>
            <div className="min-h-[10rem] self-stretch grow shrink basis-0 px-3.5 py-3 bg-white rounded-lg shadow border border-gray-300 flex-col justify-end items-start gap-2 flex">
              <textarea
                ref={textAreaRef}
                className={`self-stretch grow shrink basis-0 text-gray-900 text-base font-normal leading-normal ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder="Type notes..."
                rows="4"
                value={nextSteps}
                onChange={(e) => handleNotesChange(e.target.value)}
                onBlur={handleNotesBlur}
                aria-label="Your Recommended Next Steps"
                disabled={!canEdit}
              />
            </div>
          </div>
        )}
      </ModuleCompletionDialog>

      <GenerateReportProgressDialog
        reportStage="Diagnostic Decision"
        patientId={patientId}
        clinicianId={auth.currentUser?.uid}
        handleNextPageButtonClick={() => handleNextPageButtonClick(`/patient/${patientId}/documents`)}
        isGenerating={isGenerating}
        onCancel={handleCancelGeneration}
      />
    </Layout>
  );
}